import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/api";
import { formatearFecha } from "../utils/fechas";
import { PAGOS_ACTIVOS } from "../config/pagos";

const font = "Montserrat, sans-serif";

const SERVICE_LABELS = {
  plomeria: "Plomería", electricidad: "Electricidad", gasista: "Gasista",
  pintura: "Pintura", albanileria: "Albañilería", cerrajeria: "Cerrajería",
  limpieza: "Limpieza", jardineria: "Jardinería",
  aire_acondicionado: "Aire Acondicionado", arquitectura: "Arquitectura",
  belleza_personal: "Belleza Personal", carpinteria: "Carpintería",
  control_plagas: "Control de Plagas", cuidado_mayores: "Cuidado de Mayores",
  cuidado_ninos: "Cuidado de Niños", decoradores: "Decoradores",
  electricista: "Electricista", entrenamiento: "Entrenamiento",
  herrero: "Herrero", infantiles: "Infantiles", instalador_general: "Instalador General",
  jardinero: "Jardinero", mascotas: "Mascotas", mecanica_automotriz: "Mecánica Automotriz",
  mudanzas: "Mudanzas", piletas: "Piletas", pintores: "Pintores",
  plomeros: "Plomeros", seguridad: "Seguridad", servicio_domestico: "Servicio Doméstico",
  tapicero: "Tapicero", tecnologia: "Tecnología",
};

const SERVICE_ICONS = {
  plomeria: "🔧", plomeros: "🔧", electricidad: "⚡", electricista: "⚡",
  gasista: "🔥", pintura: "🖌️", pintores: "🖌️", albanileria: "🧱",
  cerrajeria: "🔑", limpieza: "🧺", servicio_domestico: "🧺",
  jardineria: "🌿", jardinero: "🌿", aire_acondicionado: "❄️",
  arquitectura: "🏛️", belleza_personal: "💇", carpinteria: "🪵",
  control_plagas: "🐛", cuidado_mayores: "👴", cuidado_ninos: "👶",
  decoradores: "🎨", entrenamiento: "💪", herrero: "⚒️",
  infantiles: "🎉", instalador_general: "🔩", mascotas: "🐾",
  mecanica_automotriz: "🚗", mudanzas: "📦", piletas: "🏊",
  seguridad: "🔒", tapicero: "🛋️", tecnologia: "💻",
};

const REQUEST_STATUS = {
  waiting_quotes: { label: "Esperando presupuestos", color: "#b06a00", bg: "rgba(176,106,0,0.1)" },
  quoted:         { label: "Presupuestos listos",    color: "#163244", bg: "rgba(22,50,68,0.08)" },
  assigned:       { label: "Prestador asignado",     color: "#1b7f4e", bg: "rgba(27,127,78,0.1)" },
  in_progress:    { label: "En progreso",            color: "#1b7f4e", bg: "rgba(27,127,78,0.1)" },
  completed:      { label: "Completada",             color: "#163244", bg: "rgba(22,50,68,0.08)" },
  cancelled:      { label: "Cancelada",              color: "#c0392b", bg: "rgba(192,57,43,0.1)" },
};

function formatARS(amount) {
  return new Intl.NumberFormat("es-AR", {
    style: "currency", currency: "ARS", maximumFractionDigits: 0,
  }).format(amount);
}

function remainingTime(dateStr) {
  const diff = new Date(dateStr) - Date.now();
  if (diff <= 0) return null;
  const hrs  = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  return hrs > 0 ? `${hrs}h ${mins}min` : `${mins}min`;
}

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)    return "hace un momento";
  if (diff < 3600)  return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  return `hace ${Math.floor(diff / 86400)} días`;
}

function StarRating({ rating }) {
  const r = Math.round(rating);
  return (
    <span style={{ fontSize: 12, color: "#E7A432", fontWeight: 700 }}>
      {"★".repeat(r)}{"☆".repeat(5 - r)}
      <span style={{ color: "#7a93a4", marginLeft: 4, fontWeight: 500 }}>{Number(rating).toFixed(1)}</span>
    </span>
  );
}

// ─── Confirm Modal ────────────────────────────────────────────────────────────
function ConfirmModal({ quote, action, onConfirm, onClose }) {
  const isAccept = action === "accept";
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 200,
      background: "rgba(22,50,68,0.55)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{
        background: "#fff", borderRadius: 18, padding: "32px 28px",
        width: "min(440px, 100%)",
        boxShadow: "0 16px 60px rgba(22,50,68,0.22)",
      }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{
            width: 64, height: 64, borderRadius: "50%", margin: "0 auto 18px",
            background: isAccept ? "rgba(27,127,78,0.1)" : "rgba(192,57,43,0.08)",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28,
          }}>
            {isAccept ? "✓" : "✕"}
          </div>
          <h3 style={{ margin: "0 0 10px", color: "#163244", fontSize: 18, fontWeight: 800, fontFamily: font }}>
            {isAccept ? "¿Aceptar este presupuesto?" : "¿Rechazar este presupuesto?"}
          </h3>
          <p style={{ margin: 0, color: "#7a93a4", fontSize: 14, lineHeight: 1.65, fontFamily: font }}>
            {isAccept
              ? <>Vas a contratar a <strong style={{ color: "#163244" }}>{quote.provider_name}</strong> por <strong style={{ color: "#163244" }}>{formatARS(quote.amount)}</strong>. Los demás presupuestos serán rechazados automáticamente.</>
              : <>El presupuesto de <strong style={{ color: "#163244" }}>{quote.provider_name}</strong> será rechazado. Podés seguir evaluando los otros.</>
            }
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: "12px", borderRadius: 50, border: "2px solid rgba(0,0,0,0.1)",
            background: "transparent", color: "#7a93a4", fontFamily: font, fontWeight: 600,
            fontSize: 14, cursor: "pointer",
          }}>Cancelar</button>
          <button onClick={onConfirm} style={{
            flex: 1, padding: "12px", borderRadius: 50, border: "none",
            background: isAccept ? "#163244" : "#c0392b",
            color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 14, cursor: "pointer",
          }}>
            {isAccept ? "Sí, contratar" : "Sí, rechazar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Payment Modal ────────────────────────────────────────────────────────────
function PaymentModal({ quote, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const handlePay = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/payments/checkout", { quote_id: quote.id });
      const { sandbox_init_point, init_point } = res.data;
      // En staging usamos sandbox_init_point, en producción init_point
      const url = process.env.NODE_ENV === "production" ? init_point : sandbox_init_point;
      window.location.href = url;
      onSuccess && onSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo iniciar el pago. Intentá de nuevo.");
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 200,
      background: "rgba(22,50,68,0.55)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{
        background: "#fff", borderRadius: 18, padding: "32px 28px",
        width: "min(440px, 100%)",
        boxShadow: "0 16px 60px rgba(22,50,68,0.22)",
      }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{
            width: 64, height: 64, borderRadius: "50%", margin: "0 auto 18px",
            background: "rgba(231,164,50,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28,
          }}>
            💳
          </div>
          <h3 style={{ margin: "0 0 8px", color: "#163244", fontSize: 18, fontWeight: 800, fontFamily: font }}>
            Completar pago
          </h3>
          <p style={{ margin: 0, color: "#7a93a4", fontSize: 13, lineHeight: 1.6, fontFamily: font }}>
            Serás redirigido a Mercado Pago para completar el pago de forma segura.
          </p>
        </div>

        {/* Resumen */}
        <div style={{
          background: "#f8fafb", borderRadius: 12, padding: "14px 16px",
          marginBottom: 20, border: "1px solid rgba(0,0,0,0.06)",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: "#7a93a4", fontFamily: font }}>Prestador</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#163244", fontFamily: font }}>{quote.provider_name}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: "#7a93a4", fontFamily: font }}>Servicio</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#163244", fontFamily: font }}>{quote.description?.slice(0, 40) || "Servicio FIXO"}</span>
          </div>
          <div style={{ borderTop: "1px solid rgba(0,0,0,0.06)", paddingTop: 10, marginTop: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#163244", fontFamily: font }}>Total a pagar</span>
            <span style={{ fontSize: 20, fontWeight: 900, color: "#1b7f4e", fontFamily: font }}>{formatARS(quote.amount)}</span>
          </div>
        </div>

        {/* MP badge */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          marginBottom: 18, fontSize: 12, color: "#7a93a4", fontFamily: font,
        }}>
          <span>🔒</span>
          <span>Pago seguro procesado por Mercado Pago</span>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: "rgba(192,57,43,0.08)", border: "1px solid rgba(192,57,43,0.2)",
            borderRadius: 8, padding: "10px 14px", marginBottom: 16,
            fontSize: 13, color: "#c0392b", fontFamily: font,
          }}>
            {error}
          </div>
        )}

        {/* Acciones */}
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} disabled={loading} style={{
            flex: 1, padding: "12px", borderRadius: 50, border: "2px solid rgba(0,0,0,0.1)",
            background: "transparent", color: "#7a93a4", fontFamily: font, fontWeight: 600,
            fontSize: 14, cursor: loading ? "not-allowed" : "pointer",
          }}>
            Cancelar
          </button>
          <button onClick={handlePay} disabled={loading} style={{
            flex: 2, padding: "12px", borderRadius: 50, border: "none",
            background: loading ? "#dce5ec" : "#E7A432",
            color: loading ? "#7a93a4" : "#163244",
            fontFamily: font, fontWeight: 800, fontSize: 14,
            cursor: loading ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            {loading
              ? <><span style={{ width: 14, height: 14, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} /> Redirigiendo...</>
              : "Ir a pagar →"
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Quote Card ───────────────────────────────────────────────────────────────
function QuoteCard({ quote, rank, onAccept, onReject, onPay, disabled, loading, paymentStatus }) {
  const [expanded, setExpanded] = useState(false);
  const remaining  = remainingTime(quote.valid_until);
  const isAccepted = quote.status === "accepted";
  const isRejected = quote.status === "rejected";
  const isExpired  = quote.status === "expired";
  const isBest     = rank === 0;
  const isDim      = isRejected || isExpired;
  // A propósito FUERA de PAGOS_ACTIVOS: un pago que ya pasó se informa siempre. Son tres lugares:
  // la cinta y el bloque "Pago confirmado" (isPaid) y "¡Pago confirmado!" de la página, que mira paymentStatus directo.
  const isPaid     = isAccepted && paymentStatus === "paid";
  // Sin pagos activos (config/pagos.js) no hay "pago pendiente": el presupuesto
  // aceptado es eso, aceptado, y nada del flujo depende de payment_status.
  const isPending  = PAGOS_ACTIVOS && isAccepted && paymentStatus === "pending";

  return (
    <div style={{
      background: "#fff", borderRadius: 16, marginBottom: 14,
      border: `1.5px solid ${isPaid ? "#E7A432" : isAccepted ? "#1b7f4e" : isBest && !isDim ? "#E7A432" : "rgba(0,0,0,0.08)"}`,
      boxShadow: isPaid
        ? "0 4px 20px rgba(231,164,50,0.15)"
        : isAccepted
          ? "0 4px 20px rgba(27,127,78,0.1)"
          : isBest && !isDim
            ? "0 4px 20px rgba(231,164,50,0.12)"
            : "0 2px 8px rgba(22,50,68,0.05)",
      opacity: isDim ? 0.55 : 1,
      overflow: "hidden", transition: "all 0.2s",
    }}>
      {/* Top strip */}
      {isBest && !isDim && !isAccepted && (
        <div style={{ background: "#E7A432", padding: "5px 18px", fontSize: 10, fontWeight: 800, color: "#163244", letterSpacing: "0.07em" }}>
          ⭐ MEJOR PRECIO
        </div>
      )}
      {isPaid && (
        <div style={{ background: "#E7A432", padding: "5px 18px", fontSize: 10, fontWeight: 800, color: "#163244", letterSpacing: "0.07em" }}>
          💳 PAGO CONFIRMADO
        </div>
      )}
      {isPending && (
        <div style={{ background: "#1b7f4e", padding: "5px 18px", fontSize: 10, fontWeight: 800, color: "#fff", letterSpacing: "0.07em" }}>
          ✓ PRESUPUESTO ACEPTADO — PAGO PENDIENTE
        </div>
      )}
      {isAccepted && !isPaid && !isPending && (
        <div style={{ background: "#1b7f4e", padding: "5px 18px", fontSize: 10, fontWeight: 800, color: "#fff", letterSpacing: "0.07em" }}>
          ✓ PRESUPUESTO ACEPTADO
        </div>
      )}

      <div style={{ padding: "18px 20px" }}>
        {/* Provider row */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
              background: "#163244", display: "flex", alignItems: "center",
              justifyContent: "center", fontWeight: 800, fontSize: 17, color: "#E7A432",
            }}>
              {(quote.provider_name || "?")[0]}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#163244", marginBottom: 3, display: "flex", alignItems: "center", gap: 7 }}>
                {quote.provider_name}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <StarRating rating={quote.provider_rating || 0} />
                <span style={{ fontSize: 11, color: "#aab8c2" }}>· {quote.provider_jobs || 0} trabajos</span>
              </div>
            </div>
          </div>

          {/* Monto */}
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: isBest && !isDim ? "#1b7f4e" : "#163244", lineHeight: 1, fontFamily: font }}>
              {formatARS(quote.amount)}
            </div>
            <div style={{ fontSize: 11, color: "#aab8c2", marginTop: 3 }}>
              {timeAgo(quote.created_at)}
            </div>
          </div>
        </div>

        {/* Descripción */}
        <div style={{
          background: "#f8fafb", borderRadius: 9, padding: "11px 14px",
          fontSize: 13, color: "#163244", lineHeight: 1.65,
          borderLeft: "3px solid rgba(22,50,68,0.12)", marginBottom: 12,
        }}>
          {quote.description}
        </div>

        {/* Chips */}
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 14 }}>
          {quote.estimated_start_date && (
            <div style={{ background: "#f0f5f8", borderRadius: 7, padding: "4px 11px", fontSize: 11, color: "#163244", display: "flex", gap: 5, alignItems: "center" }}>
              📅 <strong>{formatearFecha(quote.estimated_start_date)}</strong>
            </div>
          )}
          <div style={{
            background: quote.estimated_visit_required ? "rgba(176,106,0,0.08)" : "rgba(27,127,78,0.08)",
            borderRadius: 7, padding: "4px 11px", fontSize: 11,
            color: quote.estimated_visit_required ? "#b06a00" : "#1b7f4e",
          }}>
            {quote.estimated_visit_required ? "🔍 Requiere visita" : "✓ Sin visita previa"}
          </div>
          {remaining && !isAccepted && !isDim && (
            <div style={{ background: "rgba(176,106,0,0.07)", borderRadius: 7, padding: "4px 11px", fontSize: 11, color: "#b06a00" }}>
              ⏳ Válido {remaining}
            </div>
          )}
        </div>

        {/* Expandir perfil */}
        <button onClick={() => setExpanded(e => !e)} style={{
          background: "none", border: "none", cursor: "pointer", fontSize: 12,
          color: "#7a93a4", fontWeight: 600, padding: 0, fontFamily: font,
          marginBottom: expanded ? 10 : 0, display: "flex", alignItems: "center", gap: 4,
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
            <path d="M6 9l6 6 6-6"/>
          </svg>
          {expanded ? "Ocultar perfil" : "Ver perfil del prestador"}
        </button>

        {expanded && (
          <div style={{ background: "#f8fafb", borderRadius: 9, padding: "12px 14px", fontSize: 13, color: "#7a93a4", marginBottom: 12 }}>
            <div style={{ fontWeight: 700, color: "#163244", marginBottom: 6 }}>{quote.provider_name}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span>⭐ {Number(quote.provider_rating || 0).toFixed(1)} promedio · {quote.provider_jobs || 0} trabajos</span>
              {/* Acá decía "🪪 Identidad verificada · 📱 Teléfono verificado", fijo
                  para todo prestador, y FIXO no verifica identidad (Términos 5.3).
                  Se sacó el 29-10-2026 (VERIFICADO-QUE-NO-VERIFICA). Es la pantalla
                  donde el cliente elige a quién deja entrar a su casa: acá solo va
                  una señal que sea cierta para ESE prestador, leída de un campo. */}
            </div>
          </div>
        )}

        {/* Acciones — sin aceptar todavía */}
        {!isAccepted && !isDim && (
          <div style={{ display: "flex", gap: 9, marginTop: 4 }}>
            <button onClick={() => onAccept(quote)} disabled={disabled || loading} style={{
              flex: 1, padding: "11px", borderRadius: 50, border: "none",
              background: disabled ? "#dce5ec" : "#163244", color: "#fff",
              fontFamily: font, fontWeight: 700, fontSize: 13,
              cursor: disabled || loading ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              {loading === `accept-${quote.id}`
                ? <span style={{ width: 14, height: 14, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
                : "✓ Aceptar presupuesto"}
            </button>
            <button onClick={() => onReject(quote)} disabled={disabled || loading} style={{
              padding: "11px 18px", borderRadius: 50, border: "1.5px solid rgba(0,0,0,0.12)",
              background: "transparent", color: "#7a93a4", fontFamily: font,
              fontWeight: 600, fontSize: 13, cursor: disabled || loading ? "not-allowed" : "pointer",
            }}>
              Rechazar
            </button>
          </div>
        )}

        {/* Acción pagar — aceptado pero sin pagar */}
        {isPending && (
          <div style={{ marginTop: 8 }}>
            <button onClick={() => onPay(quote)} style={{
              width: "100%", padding: "13px", borderRadius: 50, border: "none",
              background: "#E7A432", color: "#163244",
              fontFamily: font, fontWeight: 800, fontSize: 14,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              💳 Completar pago — {formatARS(quote.amount)}
            </button>
          </div>
        )}

        {/* Sin pagos activos: en lugar del botón, cómo se paga de verdad. */}
        {!PAGOS_ACTIVOS && isAccepted && !isPaid && (
          <div style={{ marginTop: 8, fontSize: 12, color: "#7a93a4", lineHeight: 1.55, fontFamily: font }}>
            El pago a través de FIXO todavía no está disponible. Acordá la forma de pago directamente con el prestador.
          </div>
        )}

        {/* Pago confirmado */}
        {isPaid && (
          <div style={{
            marginTop: 8, background: "rgba(231,164,50,0.08)", borderRadius: 10,
            padding: "12px 14px", display: "flex", alignItems: "center", gap: 10,
            border: "1px solid rgba(231,164,50,0.2)",
          }}>
            <span style={{ fontSize: 20 }}>💳</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#163244", fontFamily: font }}>Pago confirmado</div>
              <div style={{ fontSize: 12, color: "#7a93a4", fontFamily: font }}>El prestador fue notificado. Coordinará con vos a la brevedad.</div>
            </div>
          </div>
        )}

        {isRejected && (
          <div style={{ fontSize: 12, color: "#c0392b", fontWeight: 600, paddingTop: 8 }}>
            ✕ Rechazaste este presupuesto
          </div>
        )}
        {isExpired && (
          <div style={{ fontSize: 12, color: "#7b5ea7", fontWeight: 600, paddingTop: 8 }}>
            ⌛ Este presupuesto expiró
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────
function Sidebar({ request, quotes, navigate }) {
  if (!request) return null;

  const reqStatus  = REQUEST_STATUS[request.status] || REQUEST_STATUS.waiting_quotes;
  const pending    = quotes.filter(q => ["submitted","viewed"].includes(q.status));
  const accepted   = quotes.find(q => q.status === "accepted");
  const amounts    = pending.map(q => q.amount).filter(Boolean);
  const cheapest   = amounts.length ? Math.min(...amounts) : null;
  const mostExp    = amounts.length ? Math.max(...amounts) : null;
  const icon       = SERVICE_ICONS[request.service_key] || "🛠️";
  const label      = SERVICE_LABELS[request.service_key] || request.service_key;

  const sCard = { background: "#fff", borderRadius: 14, border: "1px solid rgba(0,0,0,0.07)", overflow: "hidden", marginBottom: 14, fontFamily: font };
  const sHdr  = { padding: "14px 16px 0", display: "flex", alignItems: "center", gap: 8, marginBottom: 12 };
  const sAcc  = { width: 3, height: 13, background: "#E7A432", borderRadius: 2, flexShrink: 0 };
  const sTit  = { fontSize: 11, fontWeight: 700, color: "#163244", textTransform: "uppercase", letterSpacing: 1 };

  return (
    <div style={{ fontFamily: font }}>

      {/* Solicitud */}
      <div style={{ background: "#163244", borderRadius: 14, overflow: "hidden", marginBottom: 14, position: "relative" }}>
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: "radial-gradient(circle at 85% 15%, rgba(231,164,50,0.15) 0%, transparent 55%), repeating-linear-gradient(45deg, transparent, transparent 40px, rgba(255,255,255,0.015) 40px, rgba(255,255,255,0.015) 41px)" }} />
        <div style={{ padding: "18px 18px 20px", position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{icon}</div>
            <div>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>{label}</div>
              <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 11, marginTop: 1 }}>Tu solicitud</div>
            </div>
          </div>
          {request.common_data?.address && (
            <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 8, padding: "7px 11px", fontSize: 12, color: "rgba(255,255,255,0.65)", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
              📍 {request.common_data.address}
            </div>
          )}
          <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, color: reqStatus.color, background: reqStatus.bg }}>
            {reqStatus.label.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Estado contratación */}
      {accepted && (
        <div style={{ ...sCard }}>
          <div style={{ padding: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#1b7f4e", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>✓</div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14, color: "#1b7f4e" }}>¡Prestador contratado!</div>
                <div style={{ fontSize: 12, color: "#7a93a4", marginTop: 1 }}>Coordinará con vos a la brevedad.</div>
              </div>
            </div>
            <div style={{ background: "#f8fafb", borderRadius: 9, padding: "10px 12px", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#163244", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, color: "#E7A432", flexShrink: 0 }}>{(accepted.provider_name || "?")[0]}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#163244" }}>{accepted.provider_name}</div>
                <div style={{ fontSize: 12, color: "#7a93a4" }}>{formatARS(accepted.amount)}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats presupuestos */}
      {!accepted && quotes.length > 0 && (
        <div style={sCard}>
          <div style={sHdr}><div style={sAcc}/><span style={sTit}>Resumen</span></div>
          <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div style={{ background: "#f5f7fa", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#163244", lineHeight: 1 }}>{quotes.length}</div>
                <div style={{ fontSize: 10, color: "#7a93a4", marginTop: 3, fontWeight: 500 }}>Presupuestos</div>
              </div>
              <div style={{ background: "#f5f7fa", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#1b7f4e", lineHeight: 1 }}>{pending.length}</div>
                <div style={{ fontSize: 10, color: "#7a93a4", marginTop: 3, fontWeight: 500 }}>Pendientes</div>
              </div>
            </div>
            {cheapest !== null && (
              <div style={{ background: "rgba(27,127,78,0.07)", borderRadius: 10, padding: "12px 14px", border: "1px solid rgba(27,127,78,0.15)" }}>
                <div style={{ fontSize: 10, color: "#7a93a4", fontWeight: 600, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Mejor precio</div>
                <div style={{ fontSize: 18, fontWeight: 900, color: "#1b7f4e" }}>{formatARS(cheapest)}</div>
              </div>
            )}
            {cheapest !== null && mostExp !== null && cheapest !== mostExp && (
              <div style={{ background: "#f5f7fa", borderRadius: 10, padding: "10px 14px" }}>
                <div style={{ fontSize: 10, color: "#7a93a4", fontWeight: 600, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Rango de precios</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#163244" }}>{formatARS(cheapest)} – {formatARS(mostExp)}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cómo elegir */}
      {!accepted && pending.length > 0 && (
        <div style={sCard}>
          <div style={sHdr}><div style={sAcc}/><span style={sTit}>Cómo elegir</span></div>
          <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              { icon: "⭐", text: "Fijate en la calificación y cantidad de trabajos del prestador." },
              { icon: "📅", text: "Revisá la fecha de inicio estimada que cada uno propone." },
              { icon: "💬", text: "No siempre el más barato es el mejor. Leé bien la descripción." },
            ].map(({ icon: ic, text }, i) => (
              <div key={i} style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: i < 2 ? "1px solid rgba(0,0,0,0.05)" : "none" }}>
                <span style={{ fontSize: 16, flexShrink: 0, lineHeight: 1.4 }}>{ic}</span>
                <span style={{ fontSize: 12, color: "#7a93a4", lineHeight: 1.55 }}>{text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA volver */}
      <button onClick={() => navigate("/mis-solicitudes")} style={{
        width: "100%", padding: "11px", borderRadius: 50,
        background: "transparent", border: "1.5px solid rgba(22,50,68,0.2)",
        color: "#163244", fontFamily: font, fontWeight: 600,
        fontSize: 13, cursor: "pointer", marginBottom: 14,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
      }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
        Volver a mis solicitudes
      </button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ClientQuotesPage() {
  const { requestId } = useParams();
  const navigate      = useNavigate();

  const [request,      setRequest]      = useState(null);
  const [quotes,       setQuotes]       = useState([]);
  const [loadingData,  setLoadingData]  = useState(true);
  const [loadError,    setLoadError]    = useState("");
  const [modal,        setModal]        = useState(null);
  const [payModal,     setPayModal]     = useState(null);
  const [loading,      setLoading]      = useState(null);
  const [toast,        setToast]        = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    if (!requestId) return;
    setLoadingData(true); setLoadError("");
    api.get(`/matching/client/requests/${requestId}`)
      .then(res => {
        setRequest(res.data.request);
        setQuotes(res.data.quotes || []);
        setPaymentStatus(res.data.request?.payment_status || null);
      })
      .catch(() => setLoadError("No se pudieron cargar los presupuestos."))
      .finally(() => setLoadingData(false));
  }, [requestId]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3500); };

  const handleAccept = (quote) => setModal({ quote, action: "accept" });
  const handleReject = (quote) => setModal({ quote, action: "reject" });
  const handlePay    = (quote) => setPayModal(quote);

  const handleConfirm = async () => {
    const { quote, action } = modal;
    setModal(null);
    setLoading(`${action}-${quote.id}`);
    try {
      if (action === "accept") {
        await api.post(`/matching/client/quotes/${quote.id}/accept`);
        setQuotes(qs => qs.map(q =>
          q.id === quote.id ? { ...q, status: "accepted" }
          : ["submitted","viewed"].includes(q.status) ? { ...q, status: "rejected" }
          : q
        ));
        setRequest(r => r ? { ...r, status: "assigned" } : r);
        setPaymentStatus("pending");
        showToast(PAGOS_ACTIVOS
          ? `¡Contrataste a ${quote.provider_name}! Completá el pago.`
          : `¡Contrataste a ${quote.provider_name}!`);
      } else {
        await api.post(`/matching/client/quotes/${quote.id}/reject`);
        setQuotes(qs => qs.map(q => q.id === quote.id ? { ...q, status: "rejected" } : q));
        showToast("Presupuesto rechazado.");
      }
    } catch (err) {
      showToast(err.response?.data?.error || "Ocurrió un error. Intentá de nuevo.");
    } finally {
      setLoading(null);
    }
  };

  // Guards
  if (loadingData) return (
    <div style={{ minHeight: "100vh", background: "#f5f7fa", fontFamily: font, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", color: "#7a93a4" }}>
        <span style={{ display: "inline-block", width: 28, height: 28, border: "3px solid #dce5ec", borderTopColor: "#163244", borderRadius: "50%", animation: "spin 0.7s linear infinite", marginBottom: 12 }} />
        <div style={{ fontWeight: 600 }}>Cargando presupuestos...</div>
      </div>
      <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
    </div>
  );

  if (loadError || !request) return (
    <div style={{ minHeight: "100vh", background: "#f5f7fa", fontFamily: font, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontWeight: 600, color: "#c0392b", marginBottom: 16 }}>{loadError || "Solicitud no encontrada"}</div>
        <button onClick={() => navigate("/mis-solicitudes")} style={{ padding: "10px 24px", borderRadius: 50, background: "#163244", color: "#fff", border: "none", fontFamily: font, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
          Volver a mis solicitudes
        </button>
      </div>
    </div>
  );

  const hasAccepted = quotes.some(q => q.status === "accepted");

  const sorted = [...quotes].sort((a, b) => {
    const order = { accepted: -1, submitted: 0, viewed: 0, rejected: 1, expired: 2 };
    if ((order[a.status] ?? 0) !== (order[b.status] ?? 0))
      return (order[a.status] ?? 0) - (order[b.status] ?? 0);
    return a.amount - b.amount;
  });

  const pending = quotes.filter(q => ["submitted","viewed"].includes(q.status));
  const label   = SERVICE_LABELS[request.service_key] || request.service_key;
  const icon    = SERVICE_ICONS[request.service_key] || "🛠️";

  return (
    <>
      <div style={{ fontFamily: font, background: "#f5f7fa", minHeight: "100vh", padding: "28px 24px 60px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>

          {/* Breadcrumb */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20, fontSize: 13, color: "#7a93a4" }}>
            <button onClick={() => navigate("/mis-solicitudes")} style={{ background: "none", border: "none", cursor: "pointer", color: "#7a93a4", fontFamily: font, fontSize: 13, fontWeight: 500, padding: 0, display: "flex", alignItems: "center", gap: 5 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
              Mis solicitudes
            </button>
            <span style={{ color: "#dce5ec" }}>/</span>
            <span style={{ color: "#163244", fontWeight: 600 }}>{icon} {label}</span>
          </div>

          {/* Layout 2 col */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 290px", gap: 20, alignItems: "start" }}>

            {/* Columna izquierda */}
            <div>
              {/* Header */}
              <div style={{ marginBottom: 18 }}>
                <h1 style={{ margin: "0 0 4px", fontSize: 21, fontWeight: 800, color: "#163244" }}>
                  Presupuestos recibidos
                </h1>
                <p style={{ margin: 0, color: "#7a93a4", fontSize: 13 }}>
                  {paymentStatus === "paid"
                    ? "El pago fue confirmado. Tu prestador coordinará el trabajo a la brevedad."
                    : hasAccepted
                      ? (PAGOS_ACTIVOS
                          ? "Aceptaste un presupuesto. Completá el pago."
                          : "Aceptaste un presupuesto. Coordiná el trabajo y la forma de pago directamente con el prestador.")
                      : pending.length > 0
                        ? `Tenés ${pending.length} presupuesto${pending.length !== 1 ? "s" : ""} para evaluar. Están ordenados de menor a mayor precio.`
                        : "Los prestadores tienen hasta 48hs para enviarte sus propuestas."
                  }
                </p>
              </div>

              {/* Banner pago pendiente */}
              {PAGOS_ACTIVOS && hasAccepted && paymentStatus === "pending" && (
                <div style={{
                  background: "rgba(231,164,50,0.08)", border: "1.5px solid rgba(231,164,50,0.3)",
                  borderRadius: 12, padding: "14px 18px", marginBottom: 18,
                  display: "flex", alignItems: "center", gap: 12,
                }}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#E7A432", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>💳</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: "#163244" }}>Pago pendiente</div>
                    <div style={{ fontSize: 13, color: "#7a93a4", marginTop: 1 }}>Completá el pago del presupuesto aceptado.</div>
                  </div>
                </div>
              )}

              {/* Banner pago confirmado */}
              {paymentStatus === "paid" && (
                <div style={{
                  background: "rgba(27,127,78,0.08)", border: "1.5px solid rgba(27,127,78,0.2)",
                  borderRadius: 12, padding: "14px 18px", marginBottom: 18,
                  display: "flex", alignItems: "center", gap: 12,
                }}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#1b7f4e", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>✓</div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 14, color: "#1b7f4e" }}>¡Pago confirmado!</div>
                    <div style={{ fontSize: 13, color: "#7a93a4", marginTop: 1 }}>El prestador fue notificado y se comunicará con vos para coordinar.</div>
                  </div>
                </div>
              )}

              {/* Quote cards */}
              {sorted.length > 0
                ? sorted.map((q) => {
                    const rankAmongPending = sorted
                      .filter(x => ["submitted","viewed"].includes(x.status))
                      .indexOf(q);
                    return (
                      <QuoteCard
                        key={q.id}
                        quote={q}
                        rank={rankAmongPending}
                        onAccept={handleAccept}
                        onReject={handleReject}
                        onPay={handlePay}
                        disabled={hasAccepted}
                        loading={loading}
                        paymentStatus={paymentStatus}
                      />
                    );
                  })
                : (
                  <div style={{
                    textAlign: "center", padding: "56px 20px",
                    background: "#fff", borderRadius: 16,
                    border: "2px dashed rgba(0,0,0,0.08)", color: "#aab8c2",
                  }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>⏳</div>
                    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, color: "#163244" }}>
                      Esperando presupuestos
                    </div>
                    <div style={{ fontSize: 13 }}>
                      Los prestadores tienen 48 horas para enviarte sus propuestas.
                    </div>
                  </div>
                )
              }
            </div>

            {/* Sidebar */}
            <Sidebar request={request} quotes={quotes} navigate={navigate} />
          </div>
        </div>
      </div>

      {/* Modal confirmación accept/reject */}
      {modal && (
        <ConfirmModal
          quote={modal.quote}
          action={modal.action}
          onConfirm={handleConfirm}
          onClose={() => setModal(null)}
        />
      )}

      {/* Modal pago */}
      {PAGOS_ACTIVOS && payModal && (
        <PaymentModal
          quote={payModal}
          onClose={() => setPayModal(null)}
          onSuccess={() => setPayModal(null)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: "#163244", color: "#fff", padding: "13px 26px", borderRadius: 10,
          fontWeight: 600, fontSize: 14, zIndex: 999,
          boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
          borderLeft: "4px solid #E7A432", whiteSpace: "nowrap",
          fontFamily: font,
        }}>
          {toast}
        </div>
      )}

      <style>{`
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}
